import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileCheck2, ShieldCheck, Star, Trash2, Upload } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { ApiError, apiFetch } from '@/lib/api';

interface CertificatePublic {
  id: string;
  name: string;
  subjectCn: string | null;
  issuerCn: string | null;
  serialNumber: string | null;
  expiresAt: string;
  isDefault: boolean;
  daysUntilExpiry: number;
  createdAt: string;
}

interface CertificateXsd {
  id: string;
  certificateId: string;
  fileName: string;
  description: string | null;
  createdAt: string;
}

function fmt(date: string): string {
  try {
    return new Date(date).toLocaleDateString('pt-BR');
  } catch {
    return date;
  }
}

function expiryStatus(days: number): { label: string; tone: 'good' | 'warn' | 'bad' } {
  if (days < 0) return { label: 'Vencido', tone: 'bad' };
  if (days < 30) return { label: `Expira em ${days}d`, tone: 'warn' };
  return { label: `${days}d restantes`, tone: 'good' };
}

export function CertificatesPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  const qc = useQueryClient();
  const [showUpload, setShowUpload] = useState(false);
  const [showXsds, setShowXsds] = useState<CertificatePublic | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['certificates'],
    queryFn: () => apiFetch<CertificatePublic[]>('/certificates'),
  });

  const setDefault = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/certificates/${id}/default`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['certificates'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/certificates/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['certificates'] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Certificados digitais</h1>
          <p className="text-sm text-muted-foreground">
            Certificados A1 usados na assinatura da NFSe. Limite de 3 ativos. Senha cifrada com
            AES-256-GCM.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowUpload(true)}
            disabled={(data?.length ?? 0) >= 3}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            title={(data?.length ?? 0) >= 3 ? 'Limite de 3 atingido' : undefined}
          >
            <Upload className="h-4 w-4" /> Novo certificado
          </button>
        )}
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
      {isError && <p className="text-sm text-destructive">Erro ao carregar certificados.</p>}

      {data && data.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border bg-card p-12 text-center">
          <ShieldCheck className="h-10 w-10 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">Nenhum certificado cadastrado.</p>
        </div>
      )}

      {data && data.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.map((c) => {
            const status = expiryStatus(c.daysUntilExpiry);
            return (
              <article
                key={c.id}
                className="rounded-lg border border-border bg-card p-4 space-y-3"
              >
                <header className="flex items-start justify-between gap-2">
                  <div className="space-y-1 min-w-0">
                    <h3 className="font-medium truncate" data-dica={c.name}>
                      {c.name}
                    </h3>
                    {c.subjectCn && (
                      <p className="text-xs text-muted-foreground truncate" data-dica={c.subjectCn}>
                        {c.subjectCn}
                      </p>
                    )}
                  </div>
                  {c.isDefault && (
                    <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                      <Star className="h-3 w-3" /> Padrão
                    </span>
                  )}
                </header>

                <dl className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Emissor</dt>
                    <dd className="text-right truncate max-w-[60%]" data-dica={c.issuerCn ?? ''}>
                      {c.issuerCn ?? '—'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Série</dt>
                    <dd className="font-mono">{c.serialNumber ?? '—'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Expira em</dt>
                    <dd>{fmt(c.expiresAt)}</dd>
                  </div>
                </dl>

                <div
                  className={
                    status.tone === 'good'
                      ? 'flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-1 text-xs text-emerald-600'
                      : status.tone === 'warn'
                        ? 'flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-1 text-xs text-amber-600'
                        : 'flex items-center gap-1 rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive'
                  }
                >
                  {status.tone !== 'good' && <AlertTriangle className="h-3 w-3" />}
                  {status.label}
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowXsds(c)}
                    className="flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-accent"
                  >
                    <FileCheck2 className="h-3 w-3" /> XSDs
                  </button>
                  {canManage && !c.isDefault && (
                    <button
                      type="button"
                      onClick={() => setDefault.mutate(c.id)}
                      className="flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-accent"
                    >
                      <Star className="h-3 w-3" /> Tornar padrão
                    </button>
                  )}
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Excluir certificado "${c.name}"?`)) remove.mutate(c.id);
                      }}
                      className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {showUpload && <UploadCertificateDialog onClose={() => setShowUpload(false)} />}
      {showXsds && <XsdsDialog certificate={showXsds} onClose={() => setShowXsds(null)} />}
    </div>
  );
}

function UploadCertificateDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [issuerCompanyId, setIssuerCompanyId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const issuerCompanies = useQuery({
    queryKey: ['issuer-companies', 'select'],
    queryFn: () =>
      apiFetch<Array<{ id: string; razaoSocial: string; cnpj: string; isDefault: boolean }>>(
        '/issuer-companies',
      ),
  });

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Selecione um arquivo .pfx ou .p12');
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('name', name || file.name);
      fd.append('password', password);
      fd.append('isDefault', String(isDefault));
      if (issuerCompanyId) fd.append('issuerCompanyId', issuerCompanyId);
      return apiFetch<CertificatePublic>('/certificates', { method: 'POST', body: fd });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['certificates'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(err.body.includes('senha') ? 'Senha do PFX incorreta.' : err.body);
      } else if (err instanceof ApiError && err.status === 409) {
        setError('Limite de 3 certificados ativos atingido.');
      } else {
        setError(err instanceof Error ? err.message : 'Falha ao enviar.');
      }
    },
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    upload.mutate();
  }

  return (
    <Dialog title="Novo certificado A1" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="space-y-1 block">
          <span className="text-xs font-medium">Arquivo (.pfx ou .p12) *</span>
          <input
            ref={inputRef}
            type="file"
            required
            accept=".pfx,.p12,application/x-pkcs12"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm"
          />
        </label>

        <label className="space-y-1 block">
          <span className="text-xs font-medium">Nome amigável</span>
          <input
            placeholder="Ex.: Cert SimpleHotel 2026"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="space-y-1 block">
          <span className="text-xs font-medium">Senha do PFX *</span>
          <input
            type="password"
            required
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className="text-[10px] text-muted-foreground">
            Cifrada com AES-256-GCM antes de persistir. Nunca exposta novamente.
          </span>
        </label>

        <label className="space-y-1 block">
          <span className="text-xs font-medium">Empresa emissora dona deste certificado</span>
          <select
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={issuerCompanyId}
            onChange={(e) => setIssuerCompanyId(e.target.value)}
          >
            <option value="">(sem vínculo — pode usar com qualquer empresa)</option>
            {issuerCompanies.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.razaoSocial}
                {c.isDefault ? ' ★' : ''}
              </option>
            ))}
          </select>
          <span className="text-[10px] text-muted-foreground">
            Recomendado vincular — o CN do certificado deve bater com o CNPJ da empresa.
          </span>
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          Definir como padrão
        </label>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={upload.isPending || !file || !password}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {upload.isPending ? 'Enviando…' : 'Enviar'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function XsdsDialog({
  certificate,
  onClose,
}: {
  certificate: CertificatePublic;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN';
  const { data, isLoading } = useQuery({
    queryKey: ['certificates', certificate.id, 'xsds'],
    queryFn: () => apiFetch<CertificateXsd[]>(`/certificates/${certificate.id}/xsds`),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file, file.name);
      return apiFetch<CertificateXsd>(`/certificates/${certificate.id}/xsds`, {
        method: 'POST',
        body: fd,
      });
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['certificates', certificate.id, 'xsds'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/certificates/xsds/${id}`, { method: 'DELETE' }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['certificates', certificate.id, 'xsds'] }),
  });

  return (
    <Dialog title={`XSDs — ${certificate.name}`} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          XSDs vinculados ao certificado para validação local da DPS antes do envio à SEFIN.
        </p>

        {canManage && (
          <label className="block">
            <input
              type="file"
              accept=".xsd,application/xml,text/xml"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
              }}
              className="block text-sm"
            />
          </label>
        )}

        {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
        {data && data.length === 0 && (
          <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            Nenhum XSD vinculado.
          </p>
        )}
        {data && data.length > 0 && (
          <ul className="space-y-2">
            {data.map((x) => (
              <li
                key={x.id}
                className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{x.fileName}</div>
                  {x.description && (
                    <div className="text-xs text-muted-foreground">{x.description}</div>
                  )}
                </div>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Remover ${x.fileName}?`)) remove.mutate(x.id);
                    }}
                    className="rounded-md p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
